import { Cell, Table as BaseTable } from '@/components_deprecated/Table'
import type { LeaderGroupNode } from '@/modules/companies'
import { useTranslate } from '@ur-solutions/react-hooks'
import { isSameDay, parseISO } from 'date-fns'
import { Checkmark } from '@/components_deprecated/Table/cells'
import { usePermissions, useUser } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import styled, { useTheme } from 'styled-components'
import { getWeek } from '@/utils/date-fns/getWeek'
import { DietButton } from './components/DietButton'
import { useWorkPeriodApprovePermissions } from './hooks'
import { AddonCell } from '../activities/components/overview/AddonCell'
import { useWorkPeriodMigrations } from './hooks/useWorkPeriodMigrations'
import { useSalaryModuleConfiguration } from './hooks/useSalaryModuleConfiguration'
import { useSalaryApproveColumns } from './hooks/useSalaryApproveColumns'
import { ColumnNames } from '@/modules/dynamic_tables'
import { SalaryDayButton } from './components/SalaryDayButton'
import { Button, Combobox, ConditionalWrapper, H2, Tooltip } from '@/components'
import { SalarySettingSelect } from '@/components_deprecated/Select/SalarySettingSelect'
import { useState } from 'react'
import { format } from '@/utils/date-fns'
import { WorkPeriodHoursAndMap } from './WorkPeriodHoursAndMap'
import truncate from 'lodash/truncate'
import type { ApprovalRow } from './approval-rows'
import { CalendarItemCommentsButton } from './CalendarItemCommentsButton'
import { useWorkPeriodApprovalActions } from './useWorkPeriodApprovalActions'
import { useWorkPeriodDayExtensionActions } from './use-work-period-day-extension-actions'
import { getWorkPeriodDayExtensionState } from './work-period-day-extension'
import { GRAPH_WORK_PERIOD, WORK_PERIOD } from './queries'
import { ViewWorkPeriodLogsModal } from './components/view-work-period-logs-modal'
import type { WorkPeriodFlag } from './components/work-period-flags'
import { WorkPeriodFlags } from './components/work-period-flags'

const StyledCell = styled(Cell)<{ clickable?: boolean; $locked?: boolean }>`
  cursor: ${props =>
    props.$locked ? 'not-allowed' : props.clickable ? 'pointer' : 'default'};
  opacity: ${props => (props.$locked ? 0.5 : 1)};
`

const Table = styled(BaseTable)`
  thead tr {
    border-bottom: 1rem solid ${props => props.theme.colors.white};
  }
`

const CommentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  width: 100%;
`

const CommentText = styled.div`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.3;
`

const EmptyCommentAction = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: ${props => props.theme.colors.gray3};
  font-size: 0.9rem;
  font-style: italic;
`

const CommentCellInner = styled.div<{ clickable?: boolean }>`
  flex: 1;
  min-width: 0;
  max-width: 100%;
  min-height: 1.5rem;
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
`

const ApproverName = styled.div`
  margin-top: 0.15rem;
  color: ${props => props.theme.colors.gray3};
  font-size: 0.75rem;
  line-height: 1.2;
`

const ApprovalIconButton = styled.button<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
`

const GroupWrapper = styled.div`
  margin: 2rem 0;
`

const WeekHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`

const BulkApproveBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
`

const WorkPeriodBody = styled.tbody<{
  $color: string | null
  $isOpen: boolean
}>`
  && > tr:first-child {
    background-color: ${props => props.$color ?? 'transparent'};
  }

  && > tr:first-child > td {
    background-color: ${props => props.$color ?? 'transparent'};
  }

  ${props =>
    props.$isOpen &&
    `
      && > tr:nth-child(2) {
        background-color: ${props.theme.colors.gray11};
      }

      && > tr:nth-child(2) > td {
        background-color: ${props.theme.colors.gray11};
      }
    `}

  &&:hover > tr,
  &&:hover > tr > td {
    background-color: ${props => props.theme.colors.gray10};
  }
`

const TableRow = styled.tr``

const ActionCellContent = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`

const ManualHoursTooltip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 22rem;
`

const ManualHoursCommentList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin: 0;
  padding-left: 1rem;
`

const ManualHoursComment = styled.li`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

const ManualHoursTime = styled.span`
  font-weight: 600;
`

const UserCellContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 12rem;
  max-width: 100%;
  min-width: 0;
`

const UserName = styled.div`
  font-weight: 500;
  white-space: normal;
  overflow-wrap: anywhere;
`

const UserDateLine = styled.div`
  color: ${props => props.theme.colors.gray3};
  font-size: 0.9rem;
  line-height: 1.25;
`

const TimeCellContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 9rem;
  max-width: 100%;
`

const PlannedTime = styled.div`
  color: ${props => props.theme.colors.gray3};
  font-size: 0.75rem;
  line-height: 1.2;
`

const ExtensionNote = styled.div`
  color: ${props => props.theme.colors.primary500};
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
`

const ActualTime = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1;
`

const LocationDetail = styled.div`
  color: ${props => props.theme.colors.gray3};
  font-size: 0.9rem;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
`

const EstimatedBadge = styled.span`
  align-self: flex-start;
  margin-top: 0.15rem;
  padding: 0.05rem 0.4rem;
  border-radius: 0.4rem;
  background: ${props => props.theme.colors.orange};
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`

export const WorkPeriodApproveTable: React.FC<{
  approvalRows: ApprovalRow[]
  leaderGroups: LeaderGroupNode[]
  showUserColumn?: boolean
  singleUserSelected?: boolean
  loading: boolean
  initRefetch: () => void
}> = ({
  approvalRows,
  leaderGroups,
  loading,
  showUserColumn,
  singleUserSelected,
  initRefetch,
}) => {
  const translations = useTranslate({
    user: 'common.user',
    date: 'common.date',
    start: 'common.start',
    end: 'common.end',
    approvedByUser: 'common.approved-by-driver',
    approvedByManager: 'common.approved-by-manager',
    estimated: 'salary.estimated',
    comment: 'common.comment',
    managerComment: 'common.manager-comment',
    planned: 'common.planned',
    totalWork: 'common.total-work',
    weekN: ['common.week-n', { n: '0' }],
    diet: 'common.diet',
    salaryDay: 'common.salary-day',
    salaryMode: 'common.salary-mode',
    leaderGroup: 'common.leader-group',
    chooseGroup: 'common.choose-group',
    addons: 'salary.addons',
    transferedOvertimeToFlextime: 'salary.transfered-overtime-to-flextime',
    markIndividualActivitiesAsNotPaid:
      'salary.mark-individual-activities-as-not-paid',
    theSumShowsTotalWorkBeforeManuallyAddedHours:
      'salary.the-sum-shows-total-work-before-manually-added-hours',
    manualHours: 'salary.this-work-day-has-manual-hours-from-time-entries',
    ferryUsed: 'salary.work-day-includes-ferry',
    unknownVehicle: 'salary.work-day-includes-unknown-vehicle',
    lockedByManager: 'salary.locked-by-manager-approval',
    noPermissionToChange: 'salary.no-permission-to-change',
    driverMustConfirmBeforeApproval:
      'salary.driver-must-confirm-before-approval',
    commentRequiredDriverApproval: 'salary.comment-required-on-driver-approval',
    commentRequiredManagerApproval:
      'salary.comment-required-on-manager-approval',
    placeholder: 'common.standard-for-user',
    extendWorkPeriod: 'salary.extend-work-period',
    addedBefore: 'salary.extend-work-period-added-before',
    addedAfter: 'salary.extend-work-period-added-after',
    actionLog: 'salary.work-period-log',
    editedAfterLock: 'salary.edited-after-lock',
    editedAfterLockAt: ['salary.edited-after-lock-at', { date: '' }],
    approveAllForWeek: 'salary.approve-all-for-week',
    approveAllForMonth: 'salary.approve-all-for-month',
    approveAllConfirm: ['salary.approve-all-confirm', { n: 0 }],
  })

  const me = useUser()
  const theme = useTheme()
  const addPrompt = usePrompt()

  const openLogs = (workPeriodId: string) =>
    addPrompt<boolean | null>(resolve => (
      <ViewWorkPeriodLogsModal workPeriodId={workPeriodId} onClose={resolve} />
    ))
  const [openWorkPeriods, setOpenWorkPeriods] = useState<string[]>([])
  const [showMaps, setShowMaps] = useState(true)

  const salaryConfiguration = useSalaryModuleConfiguration()
  const { isEnabled: isColumnEnabled, getDisplayName } =
    useSalaryApproveColumns()
  const showSalaryMode = isColumnEnabled(ColumnNames.SALARY_NAME)
  const showEmployeeGroup = isColumnEnabled(ColumnNames.EMPLOYEE_GROUP)
  const showDiet = isColumnEnabled(ColumnNames.DIET)
  const showAddons = isColumnEnabled(ColumnNames.ADDONS)
  // Driver/manager: workflow option ON → always show (capability OR toggle)
  // Flex: feature option OFF → always hide (capability AND toggle)
  const showApprovedByDriver =
    salaryConfiguration.driverApprove ||
    isColumnEnabled(ColumnNames.APPROVED_BY_DRIVER)
  const showApprovedByManager =
    salaryConfiguration.managerApprove ||
    isColumnEnabled(ColumnNames.APPROVED_BY_MANAGER)
  const showTransferredToFlex =
    salaryConfiguration.canTransferWorkToAbsence &&
    isColumnEnabled(ColumnNames.TRANSFERRED_TO_FLEX)

  const { workPeriodApprovalState } = useWorkPeriodApprovePermissions()
  const { hasPermissions } = usePermissions()
  const canAddTimeEntry = hasPermissions(PERMISSIONS.timesheets.add.timeentry)

  const { transferOvertimeToFlex, patchWorkPeriod } = useWorkPeriodMigrations()
  const {
    openApprovalCommentFlow,
    toggleApproval,
    approveManyAsManager,
    isBulkApproving,
  } = useWorkPeriodApprovalActions({
    initRefetch,
    patchWorkPeriod,
    salaryConfiguration,
    commentRequiredDriverApproval: translations.commentRequiredDriverApproval,
    commentRequiredManagerApproval: translations.commentRequiredManagerApproval,
  })
  const { openExtendDayFlow, creatingExtension } =
    useWorkPeriodDayExtensionActions({
      initRefetch,
    })

  // Group work periods by week
  const approvalRowGroups = approvalRows.reduce(
    (acc, row) => {
      const weekStart = getWeek(parseISO(row.datetimeStart))

      if (!acc[weekStart]) {
        acc[weekStart] = []
      }
      acc[weekStart].push(row)

      return acc
    },
    {} as Record<string, ApprovalRow[]>
  )

  // Work periods the current manager may still approve (driver-confirmed where
  // required, not yet manager-approved). Feeds the "approve all" bulk actions.
  const getManagerApprovable = (rows: ApprovalRow[]) =>
    rows
      .map(row => row.workPeriod)
      .filter((workPeriod): workPeriod is NonNullable<typeof workPeriod> =>
        Boolean(workPeriod)
      )
      .filter(workPeriod => {
        const { canApproveAsManager } = workPeriodApprovalState(
          me.id,
          workPeriod
        )
        return canApproveAsManager && !workPeriod.approvedByManager
      })

  // Only offer bulk approval to a manager who has drilled into a single driver.
  const showBulkApprove = Boolean(singleUserSelected) && showApprovedByManager
  const monthApprovable = showBulkApprove
    ? getManagerApprovable(approvalRows)
    : []

  function computeColWidths(
    workPeriodGroup: ApprovalRow[],
    showUserColumn?: boolean
  ) {
    const hasUserComments = workPeriodGroup.some(
      row => !!row.workPeriod?.userComment?.trim()
    )
    const hasManagerComments = workPeriodGroup.some(
      row => !!row.workPeriod?.managerComment?.trim()
    )
    const userCommentColWidth = hasUserComments
      ? showUserColumn
        ? '17%'
        : '20%'
      : showUserColumn
      ? '8%'
      : '10%'

    const managerCommentColWidth = hasManagerComments
      ? showUserColumn
        ? '17%'
        : '20%'
      : showUserColumn
      ? '8%'
      : '10%'

    const startStopColWidth =
      !hasUserComments && !hasManagerComments
        ? showUserColumn
          ? '12%'
          : '14%'
        : showUserColumn
        ? '10%'
        : '12%'

    const userInfoColWidth = showUserColumn ? '16%' : '14%'

    return {
      startStopColWidth,
      userInfoColWidth,
      userCommentColWidth,
      managerCommentColWidth,
    }
  }

  return (
    <span>
      {showBulkApprove && monthApprovable.length > 0 && (
        <BulkApproveBar>
          <Button
            variant="outline"
            disabled={isBulkApproving}
            onClick={() => {
              void approveManyAsManager(
                monthApprovable,
                translations.approveAllConfirm({ n: monthApprovable.length })
              )
            }}
          >
            {translations.approveAllForMonth}
          </Button>
        </BulkApproveBar>
      )}
      {Object.entries(approvalRowGroups).map(group => {
        const weekNumber = group[0]
        const approvalRowGroup = group[1]

        const {
          startStopColWidth,
          userInfoColWidth,
          userCommentColWidth,
          managerCommentColWidth,
        } = computeColWidths(approvalRowGroup, showUserColumn)

        const weekApprovable = showBulkApprove
          ? getManagerApprovable(approvalRowGroup)
          : []

        return (
          <GroupWrapper key={weekNumber + approvalRowGroup[0]?.id}>
            <WeekHeader>
              <H2>{translations.weekN({ n: weekNumber })}</H2>
              {weekApprovable.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isBulkApproving}
                  onClick={() => {
                    void approveManyAsManager(
                      weekApprovable,
                      translations.approveAllConfirm({
                        n: weekApprovable.length,
                      })
                    )
                  }}
                >
                  {translations.approveAllForWeek}
                </Button>
              )}
            </WeekHeader>
            <Table
              noShadow
              noBorders
              noData={approvalRows.length === 0}
              loading={loading}
            >
              <colgroup>
                <col style={{ width: '12%' }} />
                <col style={{ width: userInfoColWidth }} />
                <col style={{ width: startStopColWidth }} />
                <col style={{ width: startStopColWidth }} />
                {showSalaryMode && <col style={{ width: '10%' }} />}
                {showEmployeeGroup && <col style={{ width: '12%' }} />}
                {showDiet && <col style={{ width: '10%' }} />}
                {showAddons && <col style={{ width: '8%' }} />}
                {showApprovedByDriver && (
                  <col style={{ width: userCommentColWidth }} />
                )}
                {showApprovedByManager && (
                  <col style={{ width: managerCommentColWidth }} />
                )}
                {showTransferredToFlex && <col style={{ width: '8%' }} />}
              </colgroup>

              <thead>
                <tr>
                  <th colSpan={2}>
                    {showUserColumn ? translations.user : translations.date}
                  </th>
                  <th>{translations.start}</th>
                  <th>{translations.end}</th>
                  {showSalaryMode && (
                    <th>
                      {getDisplayName(ColumnNames.SALARY_NAME) ||
                        translations.salaryMode}
                    </th>
                  )}
                  {showEmployeeGroup && (
                    <th>
                      {getDisplayName(ColumnNames.EMPLOYEE_GROUP) ||
                        translations.leaderGroup}
                    </th>
                  )}
                  {showDiet && (
                    <th>
                      {getDisplayName(ColumnNames.DIET) || translations.diet}
                    </th>
                  )}
                  {showAddons && (
                    <th>
                      {getDisplayName(ColumnNames.ADDONS) ||
                        translations.addons}
                    </th>
                  )}
                  {showApprovedByDriver && (
                    <th>
                      {getDisplayName(ColumnNames.APPROVED_BY_DRIVER) ||
                        translations.approvedByUser}
                    </th>
                  )}
                  {showApprovedByManager && (
                    <th>
                      {getDisplayName(ColumnNames.APPROVED_BY_MANAGER) ||
                        translations.approvedByManager}
                    </th>
                  )}
                  {showTransferredToFlex && (
                    <th>
                      {getDisplayName(ColumnNames.TRANSFERRED_TO_FLEX) ||
                        translations.transferedOvertimeToFlextime}
                    </th>
                  )}
                </tr>
              </thead>
              {approvalRowGroup.map(row => {
                if (row.type === 'calendarItem') {
                  const plannedStart = parseISO(row.calendarItem.start)
                  const plannedEnd = parseISO(row.calendarItem.end)
                  const plannedDateLines = isSameDay(plannedStart, plannedEnd)
                    ? [format(plannedStart, 'EEE dd. MMMM')]
                    : [
                        format(plannedStart, 'EEE dd. MMMM'),
                        format(plannedEnd, 'EEE dd. MMMM'),
                      ]
                  return (
                    <WorkPeriodBody key={row.id} $color="white" $isOpen={false}>
                      <TableRow>
                        <td>
                          <CalendarItemCommentsButton
                            calendarItem={row.calendarItem}
                          />
                        </td>
                        <td>
                          <UserCellContent>
                            {showUserColumn && (
                              <UserName>{row.user.fullName}</UserName>
                            )}
                            {plannedDateLines.map(dateLine => (
                              <UserDateLine key={dateLine}>
                                {dateLine}
                              </UserDateLine>
                            ))}
                            <LocationDetail>
                              {row.calendarItem.title}
                            </LocationDetail>
                          </UserCellContent>
                        </td>
                        <td width="1px">
                          <TimeCellContent>
                            <PlannedTime>{translations.planned}</PlannedTime>
                            <ActualTime>
                              {format(plannedStart, 'HH:mm')}
                            </ActualTime>
                          </TimeCellContent>
                        </td>
                        <td width="1px">
                          <TimeCellContent>
                            <PlannedTime>{translations.planned}</PlannedTime>
                            <ActualTime>
                              {format(plannedEnd, 'HH:mm')}
                            </ActualTime>
                          </TimeCellContent>
                        </td>
                        {showSalaryMode && <td />}
                        {showEmployeeGroup && <td />}
                        {showDiet && <td />}
                        {showAddons && <td />}
                        {showApprovedByDriver && <td />}
                        {showApprovedByManager && <td />}
                        {showTransferredToFlex && <td />}
                      </TableRow>
                    </WorkPeriodBody>
                  )
                }

                const workPeriod = row.workPeriod
                const {
                  driverApproveDirty,
                  managerApproveDirty,
                  canApproveAsDriver,
                  canApproveAsManager,
                  managerBlockedByDriverConfirmation,
                } = workPeriodApprovalState(me.id, workPeriod)
                const driverLocked =
                  !canApproveAsDriver && workPeriod.approvedByManager
                const managerBlockedReason = managerBlockedByDriverConfirmation
                  ? translations.driverMustConfirmBeforeApproval
                  : translations.noPermissionToChange

                let color: string | null =
                  !salaryConfiguration.driverApprove &&
                  !salaryConfiguration.managerApprove
                    ? null
                    : 'white'

                if (
                  !workPeriod.approvedByUser &&
                  salaryConfiguration.driverApprove &&
                  workPeriod.userComment
                ) {
                  color = theme.colors.red100
                }
                if (
                  !workPeriod.approvedByManager &&
                  salaryConfiguration.managerApprove &&
                  workPeriod.managerComment
                ) {
                  color = theme.colors.red100
                }

                if (workPeriod.approvedByManager) {
                  color = theme.colors.green100
                }
                // Edited after lock takes precedence — flag the row orange.
                // Opaque tint (not rgba): the row background is applied to both
                // the tr and each td, so a translucent value would stack and
                // render unevenly.
                if (workPeriod.editedAfterLock) {
                  color = '#ffeccc'
                }
                const numColumns =
                  4 +
                  (showSalaryMode ? 1 : 0) +
                  (showEmployeeGroup ? 1 : 0) +
                  (showDiet ? 1 : 0) +
                  (showAddons ? 1 : 0) +
                  (showApprovedByDriver ? 1 : 0) +
                  (showApprovedByManager ? 1 : 0) +
                  (showTransferredToFlex ? 1 : 0)
                const isOpen = openWorkPeriods.includes(workPeriod.id)
                const leaderGroupItems = [
                  ...leaderGroups.map(group => ({
                    value: group.id,
                    label: group.name,
                  })),
                ]
                if (
                  workPeriod.leaderGroup &&
                  !leaderGroupItems.some(
                    item => item.value === workPeriod.leaderGroup?.id
                  )
                ) {
                  leaderGroupItems.unshift({
                    value: workPeriod.leaderGroup.id,
                    label: workPeriod.leaderGroup.name,
                  })
                }

                const workPeriodStart = parseISO(workPeriod.datetimeStart)
                const workPeriodEnd = parseISO(workPeriod.datetimeEnd)
                const extensionState = getWorkPeriodDayExtensionState({
                  workPeriodStart,
                  workPeriodEnd,
                  timeEntries: workPeriod.timeEntries,
                })
                const plannedStart = workPeriod.calendarItem?.start
                  ? format(parseISO(workPeriod.calendarItem.start), 'HH:mm')
                  : null
                const plannedEnd = workPeriod.calendarItem?.end
                  ? format(parseISO(workPeriod.calendarItem.end), 'HH:mm')
                  : null
                const workPeriodDateLines = isSameDay(
                  workPeriodStart,
                  workPeriodEnd
                )
                  ? [format(workPeriodStart, 'EEE dd. MMMM')]
                  : [
                      format(workPeriodStart, 'EEE dd. MMMM'),
                      format(workPeriodEnd, 'EEE dd. MMMM'),
                    ]
                const startLocation = shortenLocationDetail(
                  workPeriod.startLocationDetail
                )
                const endLocation = shortenLocationDetail(
                  workPeriod.endLocationDetail
                )
                const canToggleFlexTransfer =
                  !!workPeriod.absenceHourTransaction ||
                  (salaryConfiguration.canTransferWorkToAbsence &&
                    !!workPeriod.salarySetting?.useDayOvertime &&
                    !workPeriod.salarySetting.useWeekOvertime)
                const canExtendDay =
                  canAddTimeEntry &&
                  !workPeriod.approvedByManager &&
                  !!workPeriod.user.salaryWorkTime &&
                  (canApproveAsDriver || canApproveAsManager)
                const timeEntriesWithComments = workPeriod.timeEntries.filter(
                  entry => entry.comment.trim()
                )
                const flags: WorkPeriodFlag[] = []
                if (workPeriod.editedAfterLock) {
                  flags.push({
                    key: 'edited-after-lock',
                    icon: 'exclamation-triangle',
                    severity: 'high',
                    label: workPeriod.lockedAt
                      ? translations.editedAfterLockAt({
                          date: format(
                            parseISO(workPeriod.lockedAt),
                            'dd.MM.yyyy HH:mm'
                          ),
                        })
                      : translations.editedAfterLock,
                  })
                }
                if (workPeriod.hasTimeEntries) {
                  flags.push({
                    key: 'manual-hours',
                    icon: 'pen-to-square',
                    severity: 'medium',
                    label: (
                      <ManualHoursTooltip>
                        <div>{translations.manualHours}</div>
                        {timeEntriesWithComments.length > 0 && (
                          <ManualHoursCommentList>
                            {timeEntriesWithComments.map(entry => (
                              <ManualHoursComment key={entry.id}>
                                <ManualHoursTime>
                                  {format(
                                    parseISO(entry.datetimeStart),
                                    'HH:mm'
                                  )}
                                  {entry.datetimeEnd
                                    ? ` - ${format(
                                        parseISO(entry.datetimeEnd),
                                        'HH:mm'
                                      )}`
                                    : ''}
                                  {': '}
                                </ManualHoursTime>
                                {entry.comment}
                              </ManualHoursComment>
                            ))}
                          </ManualHoursCommentList>
                        )}
                      </ManualHoursTooltip>
                    ),
                  })
                }
                if (workPeriod.hasUnknownVehicle) {
                  flags.push({
                    key: 'unknown-vehicle',
                    icon: 'truck',
                    severity: 'high',
                    label: translations.unknownVehicle,
                  })
                }
                if (workPeriod.hasFerryConditions) {
                  flags.push({
                    key: 'ferry',
                    icon: 'ferry',
                    severity: 'low',
                    label: translations.ferryUsed,
                  })
                }

                return (
                  <WorkPeriodBody
                    key={workPeriod.id}
                    $color={color}
                    $isOpen={workPeriod.salarySetting ? isOpen : false}
                  >
                    <TableRow>
                      <td>
                        <ActionCellContent>
                          <WorkPeriodFlags flags={flags} />
                          {workPeriod.salarySetting && (
                            <Button
                              size="icon-lg"
                              variant="outline"
                              onClick={() => {
                                if (isOpen) {
                                  setOpenWorkPeriods(
                                    openWorkPeriods.filter(
                                      id => id !== workPeriod.id
                                    )
                                  )
                                } else {
                                  setOpenWorkPeriods([
                                    ...openWorkPeriods,
                                    workPeriod.id,
                                  ])
                                }
                              }}
                            >
                              <Icon
                                icon="clock"
                                color={theme.colors.primary}
                                size="30"
                              />
                            </Button>
                          )}
                          {canExtendDay && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={creatingExtension}
                              onClick={() => {
                                void openExtendDayFlow(workPeriod)
                              }}
                            >
                              {translations.extendWorkPeriod}
                            </Button>
                          )}
                          {workPeriod.calendarItem && (
                            <CalendarItemCommentsButton
                              calendarItem={workPeriod.calendarItem}
                            />
                          )}
                          <Button
                            size="icon-lg"
                            variant="outline"
                            title={translations.actionLog}
                            aria-label={translations.actionLog}
                            onClick={() => openLogs(workPeriod.id)}
                          >
                            <Icon
                              icon="clock-rotate-left"
                              color={theme.colors.primary}
                              size="24"
                            />
                          </Button>
                        </ActionCellContent>
                      </td>
                      <td>
                        <UserCellContent>
                          {showUserColumn && (
                            <UserName>{workPeriod.user.fullName}</UserName>
                          )}
                          {workPeriodDateLines.map(dateLine => (
                            <UserDateLine key={dateLine}>
                              {dateLine}
                            </UserDateLine>
                          ))}
                          {workPeriod.isEstimated && (
                            <EstimatedBadge>
                              {translations.estimated}
                            </EstimatedBadge>
                          )}
                        </UserCellContent>
                      </td>
                      <td width="1px">
                        <TimeCellContent>
                          {plannedStart && (
                            <PlannedTime>
                              {translations.planned} {plannedStart}
                            </PlannedTime>
                          )}
                          <ActualTime>
                            {format(workPeriodStart, 'HH:mm')}
                          </ActualTime>
                          {extensionState.beforeExtensionStart && (
                            <ExtensionNote>
                              {translations.addedBefore}{' '}
                              {format(
                                extensionState.beforeExtensionStart,
                                'HH:mm'
                              )}
                            </ExtensionNote>
                          )}
                          {startLocation && (
                            <LocationDetail>{startLocation}</LocationDetail>
                          )}
                        </TimeCellContent>
                      </td>
                      <td width="1px">
                        <TimeCellContent>
                          {plannedEnd && (
                            <PlannedTime>
                              {translations.planned} {plannedEnd}
                            </PlannedTime>
                          )}
                          <ActualTime>
                            {format(workPeriodEnd, 'HH:mm')}
                          </ActualTime>
                          {extensionState.afterExtensionEnd && (
                            <ExtensionNote>
                              {translations.addedAfter}{' '}
                              {format(
                                extensionState.afterExtensionEnd,
                                'HH:mm'
                              )}
                            </ExtensionNote>
                          )}
                          {endLocation && (
                            <LocationDetail>{endLocation}</LocationDetail>
                          )}
                        </TimeCellContent>
                      </td>
                      {showSalaryMode && (
                        <td width="1px">
                          <SalarySettingSelect
                            value={workPeriod.salarySetting?.id || null}
                            disabled={workPeriod.approvedByManager}
                            placeholder={translations.placeholder}
                            onChange={async value => {
                              if (workPeriod.approvedByManager) return
                              await patchWorkPeriod({
                                variables: {
                                  id: workPeriod.id,
                                  input: {
                                    salarySetting: value === '' ? null : value,
                                  },
                                },
                              })
                              initRefetch()
                            }}
                          />
                          {workPeriod.salarySetting?.salaryType == 'DAY' && (
                            <SalaryDayButton
                              workPeriod={workPeriod}
                              salaryDay={workPeriod.salaryDay}
                              userId={workPeriod.user.id}
                              canEdit={
                                !workPeriod.approvedByManager &&
                                (canApproveAsManager ||
                                  !workPeriod.approvedByUser)
                              }
                              border={true}
                              onChange={() => initRefetch()}
                              restrictWidth
                            />
                          )}
                        </td>
                      )}
                      {showEmployeeGroup && (
                        <td width="1px">
                          {(leaderGroupItems.length > 0 ||
                            workPeriod.leaderGroup) && (
                            <Combobox
                              mode="single"
                              value={workPeriod.leaderGroup?.id ?? ''}
                              items={leaderGroupItems}
                              clearable
                              className="min-w-[180px]"
                              disabled={workPeriod.approvedByManager}
                              placeholder={translations.chooseGroup}
                              onChange={async value => {
                                if (workPeriod.approvedByManager) return
                                await patchWorkPeriod({
                                  variables: {
                                    id: workPeriod.id,
                                    input: {
                                      leaderGroup: value || null,
                                    },
                                  },
                                })
                                initRefetch()
                              }}
                            />
                          )}
                        </td>
                      )}
                      {showDiet && (
                        <td width="1px">
                          <DietButton
                            workPeriod={workPeriod}
                            userId={workPeriod.user.id}
                            canEdit={!workPeriod.approvedByManager}
                            border={true}
                            onChange={() => initRefetch()}
                          />
                        </td>
                      )}
                      {showAddons && (
                        <AddonCell
                          userIdOverride={workPeriod.user.id}
                          report={{
                            addonsAsText: workPeriod.addonsAsText,
                            date: workPeriod.datetimeStart,
                          }}
                          datetimeStart={workPeriodStart}
                          datetimeEnd={workPeriodEnd}
                          subColumn={false}
                          disabled={workPeriod.approvedByManager}
                        />
                      )}
                      {/* User comment / approval */}
                      {showApprovedByDriver && (
                        <StyledCell
                          narrow
                          clickable={canApproveAsDriver}
                          $locked={driverLocked}
                        >
                          <CommentRow>
                            <ConditionalWrapper
                              condition={driverLocked}
                              wrapper={c => (
                                <Tooltip content={translations.lockedByManager}>
                                  {c}
                                </Tooltip>
                              )}
                            >
                              <ApprovalIconButton
                                disabled={!canApproveAsDriver}
                                onClick={event => {
                                  event.preventDefault()
                                  event.stopPropagation()
                                  if (!canApproveAsDriver) return
                                  void toggleApproval(workPeriod, 'driver')
                                }}
                              >
                                <Checkmark
                                  checked={workPeriod.approvedByUser}
                                  icon={
                                    !driverApproveDirty
                                      ? driverLocked
                                        ? 'lock'
                                        : 'plus-circle'
                                      : undefined
                                  }
                                  color={
                                    !driverApproveDirty
                                      ? theme.colors.gray3
                                      : undefined
                                  }
                                />
                              </ApprovalIconButton>
                            </ConditionalWrapper>
                            <CommentCellInner
                              clickable={canApproveAsDriver}
                              onClick={event => {
                                event.preventDefault()
                                event.stopPropagation()
                                if (!canApproveAsDriver) return
                                void openApprovalCommentFlow(
                                  workPeriod,
                                  'driver'
                                )
                              }}
                            >
                              {workPeriod.userComment && (
                                <CommentText>
                                  {workPeriod.userComment}
                                </CommentText>
                              )}
                              {!workPeriod.userComment &&
                                canApproveAsDriver && (
                                  <EmptyCommentAction>
                                    <Icon icon="comment" />
                                    {translations.comment}
                                  </EmptyCommentAction>
                                )}
                              {workPeriod.approvedByUser &&
                                workPeriod.userApprover && (
                                  <ApproverName>
                                    {workPeriod.userApprover.fullName}
                                  </ApproverName>
                                )}
                            </CommentCellInner>
                          </CommentRow>
                        </StyledCell>
                      )}

                      {/* Manager comment / approval */}
                      {showApprovedByManager && (
                        <StyledCell
                          clickable={canApproveAsManager}
                          $locked={!canApproveAsManager}
                          narrow
                        >
                          <CommentRow>
                            <ConditionalWrapper
                              condition={!canApproveAsManager}
                              wrapper={c => (
                                <Tooltip content={managerBlockedReason}>
                                  {c}
                                </Tooltip>
                              )}
                            >
                              <ApprovalIconButton
                                disabled={!canApproveAsManager}
                                onClick={event => {
                                  event.preventDefault()
                                  event.stopPropagation()
                                  if (!canApproveAsManager) return
                                  void toggleApproval(workPeriod, 'manager')
                                }}
                              >
                                <Checkmark
                                  checked={workPeriod.approvedByManager}
                                  icon={
                                    !managerApproveDirty
                                      ? !canApproveAsManager
                                        ? 'lock'
                                        : 'plus-circle'
                                      : undefined
                                  }
                                  color={
                                    !managerApproveDirty
                                      ? theme.colors.gray3
                                      : undefined
                                  }
                                />
                              </ApprovalIconButton>
                            </ConditionalWrapper>
                            <CommentCellInner
                              clickable={canApproveAsManager}
                              onClick={event => {
                                event.preventDefault()
                                event.stopPropagation()
                                if (!canApproveAsManager) return
                                void openApprovalCommentFlow(
                                  workPeriod,
                                  'manager'
                                )
                              }}
                            >
                              {workPeriod.managerComment && (
                                <CommentText>
                                  {workPeriod.managerComment}
                                </CommentText>
                              )}
                              {!workPeriod.managerComment &&
                                canApproveAsManager && (
                                  <EmptyCommentAction>
                                    <Icon icon="comment" />
                                    {translations.comment}
                                  </EmptyCommentAction>
                                )}
                              {workPeriod.approvedByManager &&
                                workPeriod.managerApprover && (
                                  <ApproverName>
                                    {workPeriod.managerApprover.fullName}
                                  </ApproverName>
                                )}
                            </CommentCellInner>
                          </CommentRow>
                        </StyledCell>
                      )}
                      {showTransferredToFlex && (
                        <StyledCell
                          clickable={canToggleFlexTransfer}
                          narrow
                          centered
                          onClick={() => {
                            if (!canToggleFlexTransfer) return
                            void (async () => {
                              const result = await transferOvertimeToFlex({
                                variables: { workPeriod: workPeriod.id },
                                refetchQueries: [
                                  {
                                    query: WORK_PERIOD,
                                    variables: { id: workPeriod.id },
                                  },
                                  {
                                    query: GRAPH_WORK_PERIOD,
                                    variables: { id: workPeriod.id },
                                  },
                                ],
                                awaitRefetchQueries: true,
                              })
                              if (
                                result.data
                                  ?.transferOvertimeWorkToAbsenceHourTransaction
                                  .success
                              ) {
                                await initRefetch()
                              }
                            })()
                          }}
                        >
                          <Icon
                            icon={
                              workPeriod.absenceHourTransaction
                                ? 'check'
                                : 'times'
                            }
                            color={
                              workPeriod.absenceHourTransaction
                                ? theme.colors.green
                                : undefined
                            }
                          />
                        </StyledCell>
                      )}
                    </TableRow>
                    {workPeriod.salarySetting && isOpen && (
                      <TableRow>
                        <td colSpan={numColumns}>
                          <WorkPeriodHoursAndMap
                            workPeriodId={workPeriod.id}
                            activitiesOnly={
                              workPeriod.salarySetting.salaryType !== 'HOUR' &&
                              (workPeriod.salaryDay?.partOfDay ?? 0) > 0
                            }
                            showMap={showMaps}
                            onToggleMap={() => setShowMaps(value => !value)}
                          />
                        </td>
                      </TableRow>
                    )}
                  </WorkPeriodBody>
                )
              })}
            </Table>
          </GroupWrapper>
        )
      })}
    </span>
  )
}

type LocationDetailInfo = {
  description?: string | null
  postalCode?: string | null
  postalCity?: string | null
}

function shortenLocationDetail(location?: LocationDetailInfo | null) {
  if (!location) return null

  const description = truncate(
    location.description
      ?.split(',')
      .map(part => part.trim())
      .filter(Boolean)[0] ?? '',
    { length: 25 }
  )
  const place = [location.postalCode, location.postalCity]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ')

  return [description, place].filter(Boolean).join(', ') || null
}
